import { useCallback, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { ShieldCheck, Snowflake, ShieldAlert } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, RowAction } from '@/components/DataTable/types'
import { fetchSuspicions, resolveSuspicion, type AdminSuspicionRow } from '@/lib/adapters'
import { PERMISSIONS } from '@/lib/permissions'
import { StatusBadge, type StatusKind } from '@/components/StatusBadge'
import { RelativeTime } from '@/components/DataDisplay'
import { useToast } from '@/providers/UiProviders'

/**
 * BALL.03 — Ball gumondorlari (28 §5): anti-farm navbat. Har gumon vaznli-ball
 * bilan. Oqlash → holat cleared + Ball muzlatishdan chiqadi; Muzlatish → sanctioned
 * + user Ball'i muzlatiladi (POST /admin/points/suspicions/:id/resolve).
 */
export function Suspicions() {
  const t = useT()
  const { toast } = useToast()
  const [reloadKey, setReloadKey] = useState(0)

  const run = useCallback(
    async (fn: () => Promise<unknown>) => {
      try {
        await fn()
        toast(t.points.done, { tone: 'success' })
        setReloadKey((k) => k + 1)
      } catch (e) {
        toast(t.points.failed, { tone: 'error', ...(e instanceof Error && e.message ? { description: e.message } : {}) })
        throw e
      }
    },
    [toast, t],
  )

  const statusKind = (s: string): StatusKind => (s === 'cleared' ? 'active' : s === 'sanctioned' ? 'banned' : 'pending')
  const statusLabel = (s: string) =>
    s === 'cleared' ? t.points.statusCleared : s === 'sanctioned' ? t.points.statusSanctioned : t.points.statusOpen

  const columns = useMemo<AdminColumn<AdminSuspicionRow>[]>(
    () => [
      {
        key: 'user',
        header: t.points.user,
        type: 'text',
        minWidth: 160,
        render: (r) => (
          <Link to={`/users/${r.user_id}`} className="text-accent hover:underline">
            {r.user_name || `#${r.user_id}`}
          </Link>
        ),
      },
      { key: 'reason', header: t.points.reason, type: 'text', minWidth: 200 },
      {
        key: 'score',
        header: t.points.score,
        type: 'number',
        width: 90,
        align: 'right',
        render: (r) => <span className="block text-right font-medium text-danger">{r.score}</span>,
      },
      {
        key: 'status',
        header: t.points.enabled,
        type: 'enum',
        width: 140,
        filterable: true,
        options: [
          { value: 'open', label: t.points.statusOpen, status: 'pending' },
          { value: 'cleared', label: t.points.statusCleared, status: 'active' },
          { value: 'sanctioned', label: t.points.statusSanctioned, status: 'banned' },
        ],
        render: (r) => <StatusBadge status={statusKind(r.status)} label={statusLabel(r.status)} />,
      },
      {
        key: 'created_at',
        header: t.points.done,
        type: 'date',
        width: 150,
        render: (r) => <RelativeTime value={r.created_at} className="text-secondary" />,
      },
    ],
    [t],
  )

  const rowActions = useMemo<RowAction<AdminSuspicionRow>[]>(
    () => [
      {
        id: 'clear',
        label: t.points.clear,
        icon: <ShieldCheck size={13} />,
        visible: (r) => r.status === 'open',
        onSelect: (r) => void run(() => resolveSuspicion(r.id, false)),
      },
      {
        id: 'sanction',
        label: t.points.sanction,
        icon: <Snowflake size={13} />,
        danger: true,
        visible: (r) => r.status === 'open',
        onSelect: (r) => void run(() => resolveSuspicion(r.id, true)),
      },
    ],
    [t, run],
  )

  return (
    <>
      <PageHeader
        title={t.points.suspicions}
        screenId="BALL.03"
        actions={
          <Link to="/points/earn-rules" className="text-sm text-accent hover:underline">
            {t.points.title}
          </Link>
        }
      />
      <DataTable<AdminSuspicionRow>
        key={reloadKey}
        screenId="BALL.03"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetchSuspicions}
        rowId={(r) => String(r.id)}
        rowActions={rowActions}
        emptyState={{ icon: <ShieldAlert size={22} />, title: t.points.suspicions }}
      />
    </>
  )
}
